import { faDigits } from './numerals'

/**
 * The public site's rules (design "Tooti Web": Landing, About, Contact,
 * Privacy, the login frame). Pure, so each is a unit test.
 *
 * The mailboxes are on the site's own domain — the privacy page has
 * published privacy@parrots.club since #87, and the design's
 * `@tooti.app` addresses are explicitly placeholders. A mailbox that does
 * not exist yet is a hosting task, not a reason to print a domain the
 * product does not own.
 */
export const SUPPORT_EMAIL = 'support@parrots.club'
export const PRESS_EMAIL = 'press@parrots.club'
export const PRIVACY_EMAIL = 'privacy@parrots.club'

/** A number the design's way for the current language: Persian digits in fa, Latin otherwise. */
export function localizedNumber(value, language) {
  const text = String(value)
  return language === 'fa' ? faDigits(text) : text
}

/**
 * The contact form as a `mailto:` — the fallback when `contact/send` did not
 * deliver (no bot configured, Telegram down, no network). A form that says
 * "sent" while nothing was sent is the one thing a contact page must never
 * do, so the page only claims delivery on the server's 200 and otherwise
 * offers this link. The subject is prefixed so the mailbox can filter site
 * mail; the sender's name and reply address go under the message, since a
 * mail client fills the From header itself.
 */
export function contactMailto({ to = SUPPORT_EMAIL, subject = '', name = '', reply = '', message = '' }) {
  const parts = [message.trim()]
  const signature = [name.trim(), reply.trim()].filter(Boolean).join(' · ')
  if (signature) parts.push('', signature)
  const query = new URLSearchParams({
    subject: `[Tooti] ${subject.trim()}`.trim(),
    body: parts.join('\n'),
  })
  // URLSearchParams encodes spaces as "+", which mail clients read
  // literally; percent-encoding is what the mailto scheme expects.
  return `mailto:${to}?${query.toString().replace(/\+/g, '%20')}`
}

/**
 * Which outcome the contact page shows when `contact/otp-request` or
 * `contact/send` rejected.
 *
 * - `otpThrottled` (429 `otp_throttled`): too many codes for this number or
 *   from this connection; waiting is the fix.
 * - `rateLimited` (any other 429): too many messages from this connection.
 * - `otpInvalid` (422 `otp_invalid`): the code is wrong, expired or used.
 * - `countryNotLive` (422 `country_not_live`): a number from a country the
 *   product does not serve yet.
 * - `mailFallback` (503 `contact_not_configured`): the server has no bot
 *   yet — a standing state, not a fault, and no SMS was paid for, so the
 *   page does what it did before the endpoint existed: opens the mail client.
 * - `failed`: anything else (502 Telegram down, the SMS budget spent, a
 *   network error with no response at all). Ours; the mail client is the
 *   way out and the visitor chooses.
 *
 * @param {{ response?: { status?: number, data?: { error?: string } } }|null|undefined} error The axios error.
 * @returns {'otpThrottled'|'rateLimited'|'otpInvalid'|'countryNotLive'|'mailFallback'|'failed'}
 */
export function contactFailureKind(error) {
  const status = error?.response?.status
  const code = error?.response?.data?.error
  if (status === 429) return code === 'otp_throttled' ? 'otpThrottled' : 'rateLimited'
  if (status === 422 && code === 'otp_invalid') return 'otpInvalid'
  if (status === 422 && code === 'country_not_live') return 'countryNotLive'
  if (status === 503 && code === 'contact_not_configured') return 'mailFallback'
  return 'failed'
}
